import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertIcon, CheckIcon, CloseIcon, InfoIcon } from '@/components/icons'
import { toastReducedVariants, toastVariants, usePrefersReducedMotion } from '@/design-system/motion'
import { IconButton } from './IconButton'
import { cx } from './internal/cx'
import './Toast.css'

export type ToastTone = 'success' | 'error' | 'info'

export type ToastInput = {
  message: string
  tone?: ToastTone
}

export type ToastContextValue = {
  showToast: (toast: ToastInput) => void
}

type ActiveToast = {
  id: number
  message: string
  tone: ToastTone
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TONE_ICON: Record<ToastTone, ReactNode> = {
  success: <CheckIcon size={18} />,
  error: <AlertIcon size={18} />,
  info: <InfoIcon size={18} />,
}

const AUTO_DISMISS_MS = 4000
const MAX_VISIBLE = 3

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([])
  const nextId = useRef(0)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())
  const reducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach((timer) => clearTimeout(timer))
      pending.clear()
    }
  }, [])

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.delete(id)
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback(
    ({ message, tone = 'info' }: ToastInput) => {
      nextId.current += 1
      const id = nextId.current
      setToasts((current) => [...current.slice(-(MAX_VISIBLE - 1)), { id, message, tone }])
      const timer = setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
      timers.current.set(id, timer)
    },
    [dismiss],
  )

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {createPortal(
        <div className="ui-toast-region">
          <AnimatePresence>
            {toasts.map((toast) => (
              <motion.div
                key={toast.id}
                className={cx('ui-toast', `ui-toast--${toast.tone}`)}
                role={toast.tone === 'error' ? 'alert' : 'status'}
                variants={reducedMotion ? toastReducedVariants : toastVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <span className="ui-toast__icon">{TONE_ICON[toast.tone]}</span>
                <span className="ui-toast__message">{toast.message}</span>
                <IconButton aria-label="Sluit melding" onClick={() => dismiss(toast.id)}>
                  <CloseIcon size={16} />
                </IconButton>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>')
  }
  return context
}
